import { jsx as _jsx } from "react/jsx-runtime";
import { useIsLoggedIn } from "@air/utils-auth";
import { reportErrorToBugsnag } from "@air/utils-error";
import { useQueryClient } from "@tanstack/react-query";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { usePreviousDistinct, useSessionStorage } from "__barrel_optimize__?names=usePreviousDistinct,useSessionStorage!=!react-use";
import { useAccountUsedOrganizationSAML } from "~/swr-hooks/account/useAccountUsedOrganizationSAML";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { getWorkspacesListKey } from "~/swr-hooks/workspaces/useWorkspaces";
import { useAccountProvisioner } from "../hooks/useAccountProvisioner";
const defaultValue = {
    accountProvisioner: null,
    continuedWithAccountProvisioningError: false,
    setContinuedWithAccountProvisioningError: ()=>{
    /**/ },
    inProgress: false,
    hasError: false,
    isFetchPending: true
};
const AccountProvisionerProviderContext = /*#__PURE__*/ createContext(defaultValue);
export const AccountProvisionerProvider = (param)=>{
    let { children } = param;
    const { isLoggedIn, isLoggedInFetched } = useIsLoggedIn();
    const { data: accountProvisioner = null, isFetched: isAccountProvisionerFetched, isError, error } = useAccountProvisioner();
    const { accountUsedOrganizationSAML } = useAccountUsedOrganizationSAML();
    const prevStatus = usePreviousDistinct(accountProvisioner === null || accountProvisioner === void 0 ? void 0 : accountProvisioner.status);
    const queryClient = useQueryClient();
    const hasError = isError || !!accountProvisioner && [
        "failed",
        "exception"
    ].includes(accountProvisioner.status);
    const inProgress = !!accountProvisioner && [
        "pending",
        "active"
    ].includes(accountProvisioner.status);
    const isFetchPending = accountUsedOrganizationSAML && !(isLoggedInFetched && !isLoggedIn) && !(isLoggedInFetched && isLoggedIn && isAccountProvisionerFetched);
    const [continuedWithAccountProvisioningError, setContinuedWithAccountProvisioningError] = useSessionStorage("continuedWithAccountProvisioningError");
    // Invalidate workspaces list query when accountProvisioner status changes to 'succeeded'
    useEffect(()=>{
        const currentStatus = accountProvisioner === null || accountProvisioner === void 0 ? void 0 : accountProvisioner.status;
        if (prevStatus && currentStatus === "succeeded" && prevStatus !== currentStatus) {
            queryClient.invalidateQueries({
                queryKey: getWorkspacesListKey()
            });
        }
    }, [
        prevStatus,
        accountProvisioner === null || accountProvisioner === void 0 ? void 0 : accountProvisioner.status,
        queryClient
    ]);
    const value = useMemo(()=>({
            accountProvisioner,
            continuedWithAccountProvisioningError,
            setContinuedWithAccountProvisioningError,
            inProgress,
            hasError,
            error,
            isFetchPending
        }), [
        accountProvisioner,
        continuedWithAccountProvisioningError,
        error,
        hasError,
        inProgress,
        isFetchPending,
        setContinuedWithAccountProvisioningError
    ]);
    return /*#__PURE__*/ _jsx(AccountProvisionerProviderContext.Provider, {
        value: value,
        children: children
    });
};
export function useAccountProvisionerContext() {
    const context = useContext(AccountProvisionerProviderContext);
    if (context === defaultValue) {
        const error = "AccountProvisionerProviderContext used outside of AccountProvisionerProvider";
        if (isDevOrTestStage()) {
            throw error;
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
}
